import App from "@/App";
import { UserProvider } from "@/context/UserContext";
import { useTheme } from "@/context/useTheme";
import { useTranslationLoader } from "@/hooks/useTranslationLoader";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, Spin } from "antd";
import enUS from "antd/es/locale/en_US";
import hiIN from "antd/es/locale/hi_IN";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: false },
  },
});

const getAntdLocale = (lng: string) => {
  switch (lng) {
    case "hi":
      return hiIN;
    case "en":
    default:
      return enUS;
  }
};

export const ConfigWrapper = () => {
  const { theme } = useTheme();
  const { i18n } = useTranslation();
  const { isLoading, isLoaded } = useTranslationLoader();
  const antdLocale = getAntdLocale(i18n.language);

  // Ensure i18n is initialized - wait a bit for it to be ready
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    // Ensure i18n is ready before rendering
    if (i18n.isInitialized) {
      setI18nReady(true);
    } else {
      // Wait for i18n to initialize
      const checkReady = setInterval(() => {
        if (i18n.isInitialized) {
          setI18nReady(true);
          clearInterval(checkReady);
        }
      }, 100);

      // Timeout after 2 seconds
      setTimeout(() => {
        clearInterval(checkReady);
        setI18nReady(true); // Render anyway to prevent infinite loading
      }, 2000);

      return () => clearInterval(checkReady);
    }
  }, [i18n]);

  // Show loading spinner while translations are being loaded or i18n is not ready
  if ((isLoading && !isLoaded) || !i18nReady) {
    return <Spin size="large" tip="Loading translations..." fullscreen />;
  }

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        hashed: false,
        ...theme,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <App />
        </UserProvider>
      </QueryClientProvider>
    </ConfigProvider>
  );
};
